import React, { FC, MutableRefObject, ReactElement, useCallback, useContext, useMemo, useRef, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { KeyboardCode } from '@dnd-kit/core'
import {
    FormProvider,
    useForm,
    UseFormMethods,
    ValidateResult
} from '../../../../Vendor/react-hook-form/dist/index.ie11'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, PromptContext, ReactState } from '../../../../React/Common/Contexts'
import { Authentication } from '../../../../../i18n'
import { LoginFormType } from '../../../Types/Types'
import {
    CME_FIELD_NAME,
    LOCALE_FIELD_NAME,
    PASSWORD_FIELD_NAME,
    REMEMBER_ME_FIELD_NAME,
    SERVER_FIELD_NAME,
    USERNAME_FIELD_NAME
} from './Constants/FieldNames'
import { RoundedButton } from '../../Atoms/RoundedButton/RoundedButton'
import { EMPTY_STRING } from '../../../../NonReact/constants/Common'
import { OutlinedTextInput } from '../../Atoms/Inputs/OutlinedTextInput'
import { OutlinedPasswordTextInput } from '../../Atoms/Inputs/OutlinedPasswordTextInput'
import { RouterCheckbox } from '../../Atoms/Inputs/RouterCheckbox'
import { ValidationErrors } from '../../../../React/Common/Components/ValidationErrors/ValidationErrors'
import { isStringNotEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { $LocalStorage, getCookie, hasCookie } from '../../../../NonReact/utils/StorageUtils'
import { LOCALE_COOKIE, LoginState, USERNAME_MEMORY } from './Constants/Constants'
import { LoginResponse, signIn } from '../../../../React/Common/API/UserSignInOutAPI'
import { displayError, displayErrorMessage } from '../../../../React/Common/Utils/Messages'
import { LOCALE_EN_GB } from '../../../../React/Common/Constants/Locale'
import { LocaleSwitcher } from '../../Atoms/LocaleSwitcher/LocaleSwitcher'
import { Branding } from '../../../../React/Common/API/BrandingAPI'
import { SignInWith } from '../SignInWith/SignInWith'
import { ON_CHANGE } from '../../../../React/Common/Constants/HookFormEventNames'
import { attemptBroadworksSSOLogin } from '../SignInWith/BroadworksSSOUtils'
import { BroadworksSSOConfigDTO, LoginLandingPageFlags } from '../../../../NonReact/Types/Types'
import { useBroadworksSSOConfig } from '../../../../React/Common/Hooks/useBroadworksSSOConfig'
import { RouterAnchor } from '../../Atoms/Anchor/RouterAnchor'
import { requestGetJSON, RESTAPIError, StatusCodes } from '../../../../NonReact/common/API/REST/RESTAPIClient'
import {
    APIElements,
    availableServicesForUser,
    userLoginLandingUserCheckURL
} from '../../../../NonReact/constants/ApiConstants'
import { useLoginLandingPageFlags } from '../../../../React/Common/Hooks/useLoginLandingPageFlags'
import { OutlinedSelect } from '../../Atoms/Inputs/OutlinedSelect'
import { CoreAppVersion, ServiceEntries, ServiceEntry, UserLookupResponse } from './Types/Types'
import { SelectOption } from '../../../../React/Common/Components/Selects/Types'
import { NONE_VALUE_STRING_DEFAULT } from '../../../../React/Common/Constants/DefaultValues'
import { getQueryParam, navigateTopWindow } from '../../../../NonReact/utils/LocationUtils'
import { moveToURL } from '../../../../NonReact/constants/LocationUtils'
import { URLConstructor } from '../../../../NonReact/utils/URLBuilder'
import { APP_PATH, REVERSION_SERVLET_COMMAND, SERVLET_COMMAND, ServletCmds } from '../../../../NonReact/constants/ServletCommands'
import Loader from '../../../../React/Common/Components/Loader'
import { RouteID } from '../Header/Types/Types'
import { PromptDisplayType } from '../../../../React/Common/Contexts/PromptContext'
import { AutocompleteValues } from '../../../../NonReact/constants/FormConstants'
import { getURLFromRedirectCommand } from '../../../Constants/ApiConstants'
import { APIData, JSONResponse } from '../../../../NonReact/common/Types/Types'
import { getNewUIConfig, NewUIConfigDTO } from '../../../../React/Common/API/GenericConfigAPI'
import { buildNewUiSsoUrl, FUSION_TOKEN_QUERY_PARAM } from '../Header/Constants'
import { NewUISsoResponse } from '../Header/Types/Types'

type Props = {
    branding: Nullable<Branding>
}

export const LoginForm: FC<Props> = ({ branding }: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    const { displayPromptPromise }: PromptDisplayType = useContext(PromptContext)

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const broadworksSSOConfig: Nullable<BroadworksSSOConfigDTO> = useBroadworksSSOConfig()

    const loginLandingPageFlags: Nullable<LoginLandingPageFlags> = useLoginLandingPageFlags()

    const usernameFromURL: Nullable<string> = useMemo((): Nullable<string> => getQueryParam('username'), [])
    const viewFromURL: Nullable<string> = useMemo((): Nullable<string> => getQueryParam('view'), [])

    const isLoginLandingPage: boolean = useMemo(
        (): boolean => loginLandingPageFlags?.loginLandingEnabled ?? false,
        [loginLandingPageFlags]
    )

    const isLoginLandingPageALB: boolean = useMemo(
        (): boolean => loginLandingPageFlags?.loginLandingALBEnabled ?? false,
        [loginLandingPageFlags]
    )

    const [loginLandingPageView, setLoginLandingPageView]: ReactState<LoginState> = useState<LoginState>(
        isStringNotEmpty(viewFromURL) && !Number.isNaN(Number(viewFromURL))
            ? Number(viewFromURL)
            : LoginState.VIEW_USERNAME
    )
    const [loginLandingPageUrlRedirectHash, setLoginLandingPageUrlRedirectHash]: ReactState<Nullable<string>> =
        useState<Nullable<string>>(null)

    const [isSubmitting, setIsSubmitting]: ReactState<boolean> = useState<boolean>(false)
    const [broadworksSSORegEx, setBroadworksSSORegEx]: ReactState<Nullable<RegExp>> = useState<Nullable<RegExp>>()
    const [isLoadingServices, setIsLoadingServices]: ReactState<boolean> = useState<boolean>(false)
    const [displayServices, setDisplayServices]: ReactState<Nullable<ServiceEntries>> = useState<
        Nullable<ServiceEntries>
    >([])
    const [availableServices, setAvailableServices]: ReactState<Nullable<Record<string, ServiceEntry>>> =
        useState<Nullable<Record<string, ServiceEntry>>>()

    const isBroadworksSignInMode: boolean = useMemo(
        (): boolean => isVarNotEmpty(broadworksSSORegEx),
        [broadworksSSORegEx]
    )

    const isLandingView: boolean = useMemo(
        (): boolean =>
            (isLoginLandingPage || isLoginLandingPageALB) &&
            (loginLandingPageView === LoginState.VIEW_USERNAME || loginLandingPageView === LoginState.VIEW_SERVER_CME),
        [isLoginLandingPage, isLoginLandingPageALB, loginLandingPageView]
    )

    const initialLocaleValue: string = useMemo((): string => {
        if (hasCookie(LOCALE_COOKIE)) {
            const cookie: string = getCookie(LOCALE_COOKIE)

            if (isStringNotEmpty(cookie) && /[a-z]{2}_[A-Z]{2}/g.test(cookie)) {
                return cookie
            }
        }

        return LOCALE_EN_GB
    }, [])

    const defaultValues: MutableRefObject<LoginFormType> = useRef<LoginFormType>({
        username: isStringNotEmpty(usernameFromURL)
            ? usernameFromURL
            : $LocalStorage.getItem(USERNAME_MEMORY, EMPTY_STRING) ?? EMPTY_STRING,
        password: EMPTY_STRING,
        server: NONE_VALUE_STRING_DEFAULT,
        cme: EMPTY_STRING,
        rememberMe: isStringNotEmpty($LocalStorage.getItem(USERNAME_MEMORY, EMPTY_STRING)),
        locale: initialLocaleValue
    })

    const methods: UseFormMethods<LoginFormType> = useForm({
        mode: ON_CHANGE,
        defaultValues: defaultValues.current
    })

    const { handleSubmit, setValue, register, clearErrors }: UseFormMethods<LoginFormType> = methods

    const refreshView = useCallback(
        (url: string, email: string, server?: string): void => {
            if (isLoginLandingPageALB) {
                const selectedServerDescription: Definable<string> = availableServices?.[url]?.description
                moveToURL(
                    URLConstructor.newInstance({
                        pathParams: [window.location.pathname],
                        queryParams: {
                            username: email,
                            view: 2,
                            server: selectedServerDescription ?? server ?? undefined
                        }
                    })
                )
            } else {
                const destinationVersion: string = availableServices?.[url]?.version ?? CoreAppVersion.V3
                if (destinationVersion === CoreAppVersion.V2) {
                    moveToURL(
                        URLConstructor.newInstance({
                            baseURL: url,
                            pathParams: [APP_PATH],
                            queryParams: {
                                [SERVLET_COMMAND]: ServletCmds.AUTHENTICATION,
                                [REVERSION_SERVLET_COMMAND]: ServletCmds.MAIN_HOME,
                                tbUserName: email
                            }
                        })
                    )
                } else {
                    moveToURL(
                        URLConstructor.newInstance({
                            baseURL: url,
                            pathParams: [window.location.pathname],
                            queryParams: {
                                username: email,
                                view: 2
                            }
                        })
                    )
                }
            }
        },
        [isLoginLandingPageALB, availableServices]
    )

    const servicesToMap = (services: Nullable<ServiceEntries>): void => {
        if (isVarNotEmpty(services) && isMounted.current) {
            setAvailableServices(
                services.reduce(
                    (acc: Record<string, ServiceEntry>, service: ServiceEntry): Record<string, ServiceEntry> => ({
                        ...acc,
                        [service.url]: service
                    }),
                    {}
                )
            )
        }
    }

    const performServiceLookup = useCallback((username: string, nonAdminErrorHandler?: () => void): Promise<void> => {
        if (isStringNotEmpty(username) && isMounted.current) {
            setIsLoadingServices(true)
            return requestGetJSON<ServiceEntries>(availableServicesForUser(username))
                .then((services: Nullable<ServiceEntries>): void => {
                    if (isMounted.current) {
                        setLoginLandingPageView(LoginState.VIEW_SERVER_CME)
                        setDisplayServices(services)
                        servicesToMap(services)
                    }
                })
                .catch((error: RESTAPIError): void => {
                    if (isMounted.current) {
                        if (error.HTTPStatus === StatusCodes.FORBIDDEN) {
                            setLoginLandingPageView(LoginState.VIEW_USERNAME)
                            displayErrorMessage(language.get(Authentication.LoginForm_LoginLanding_UserNonAdmin_Error))
                            if (isVarNotEmpty(nonAdminErrorHandler)) {
                                nonAdminErrorHandler()
                            }
                            return
                        }

                        if (error.HTTPStatus === StatusCodes.UNAUTHORISED) {
                            const returnedServices: Nullable<ServiceEntries> =
                                error.JSONObject as Nullable<ServiceEntries>

                            if (Array.isArray(returnedServices)) {
                                setLoginLandingPageView(LoginState.VIEW_SERVER_CME)
                                setDisplayServices(returnedServices)
                                servicesToMap(returnedServices)
                                return
                            }
                        }

                        displayError(error)
                    }
                })
                .finally((): void => {
                    if (isMounted.current) {
                        setIsLoadingServices(false)
                    }
                })
        }

        return Promise.resolve()
    }, [])

    const makeLoginLandingPageCheckRequest = (
        email: string,
        cme?: string
    ): Promise<Nullable<APIData<UserLookupResponse>>> =>
        requestGetJSON<APIData<UserLookupResponse>>(userLoginLandingUserCheckURL(email, cme))

    const performUserLandingLookup = useCallback(
        (email: string, server?: string, cme?: string): void => {
            if (isStringNotEmpty(server) && server !== NONE_VALUE_STRING_DEFAULT) {
                refreshView(server, email)
            } else {
                makeLoginLandingPageCheckRequest(email, cme)
                    .then((response: Nullable<APIData<UserLookupResponse>>): void => {
                        refreshView(response?.Data?.url ?? EMPTY_STRING, email, response?.Data?.description)
                    })
                    .catch((error: RESTAPIError): void => {
                        if (error.HTTPStatus === StatusCodes.NOT_FOUND) {
                            displayErrorMessage(
                                language.get(Authentication.LoginForm_LoginLanding_User_Not_Found_Error)
                            )
                            return
                        }
                        performServiceLookup(email)
                    })
            }
        },
        [isLoginLandingPageALB, availableServices]
    )

    const successfulSubmit = useCallback(
        (data: LoginFormType): void => {
            const shouldRedirectWithHash: boolean =
                data.username !== EMPTY_STRING &&
                data.server !== EMPTY_STRING &&
                isVarNotEmpty(loginLandingPageUrlRedirectHash) &&
                loginLandingPageUrlRedirectHash !== EMPTY_STRING

            if (shouldRedirectWithHash) {
                const url: string = URLConstructor.newInstance({
                    baseURL: data.server,
                    pathParams: [window.location.pathname],
                    queryParams: {
                        username: data.username,
                        view: LoginState.VIEW_PASSWORD
                    }
                }).toString()

                window.location.assign(`${url}${loginLandingPageUrlRedirectHash}`)
                return
            }

            if (
                (isLoginLandingPage || isLoginLandingPageALB) &&
                (loginLandingPageView === 0 || loginLandingPageView === 1)
            ) {
                performUserLandingLookup(data.username, data?.server, data?.cme)
                return
            }

            if (isStringNotEmpty(data.username) && data.rememberMe) {
                $LocalStorage.setItem(USERNAME_MEMORY, data.username)
            }

            if (!data.rememberMe) {
                $LocalStorage.removeItem(USERNAME_MEMORY)
            }

            setIsSubmitting(true)

            if (isVarNotEmpty(broadworksSSORegEx)) {
                attemptBroadworksSSOLogin(data.username, broadworksSSOConfig).catch((e: Error): void => {
                    displayError(e)

                    if (isMounted.current) {
                        setIsSubmitting(false)
                    }
                })
            } else {
                signIn(data.username, data.password, data.locale)
                    .then((response: Nullable<LoginResponse>): void => {
                        const performCcsRedirect = (redirectPath?: string): void =>
                            moveToURL(getURLFromRedirectCommand(redirectPath ?? RouteID.AUTH, APIElements.AKIXI_2019))

                        const performCcsRedirectWithPrompt = (): void => {
                            if (isStringNotEmpty(response?.Message)) {
                                displayPromptPromise(
                                    <div>
                                        <h2>{response?.MainTitle}</h2>
                                        <p>{response?.Message}</p>
                                    </div>,
                                    undefined,
                                    undefined,
                                    undefined,
                                    true
                                ).then((): void => performCcsRedirect(response?.RedirectThePageTo))
                            } else {
                                performCcsRedirect(response?.RedirectThePageTo)
                            }
                        }

                        if (response?.newUi === 'true') {
                            Promise.all([
                                requestGetJSON<NewUISsoResponse>(buildNewUiSsoUrl()),
                                getNewUIConfig()
                            ])
                                .then(
                                    ([ssoResponse, config]: [
                                        JSONResponse<NewUISsoResponse>,
                                        JSONResponse<NewUIConfigDTO>
                                    ]): void => {
                                        const sNewUiUrl: string = config?.url?.trim() ?? EMPTY_STRING

                                        if (ssoResponse?.session_token && isStringNotEmpty(sNewUiUrl)) {
                                            const pNewUiURL: URL = new URL(sNewUiUrl, window.location.origin)
                                            pNewUiURL.searchParams.set(
                                                FUSION_TOKEN_QUERY_PARAM,
                                                ssoResponse.session_token
                                            )
                                            navigateTopWindow(pNewUiURL.toString())
                                        } else {
                                            performCcsRedirectWithPrompt()
                                        }
                                    }
                                )
                                .catch((): void => performCcsRedirectWithPrompt())
                        } else {
                            performCcsRedirectWithPrompt()
                        }
                    })
                    .catch((e: Error): void => {
                        displayError(e)

                        if (isMounted.current) {
                            setIsSubmitting(false)
                        }
                    })
            }
        },
        [
            broadworksSSORegEx,
            broadworksSSOConfig,
            isLoginLandingPageALB,
            isLoginLandingPage,
            loginLandingPageView,
            availableServices,
            branding,
            location,
            loginLandingPageUrlRedirectHash
        ]
    )

    return isSubmitting ? (
        <Loader />
    ) : (
        <div className='login-form'>
            {isStringNotEmpty(branding?.BrandingConfig?.imagery?.squareLogo) ? (
                <div className='lf-cx-logo-container'>
                    <img className='lf-cx-logo' src={branding.BrandingConfig.imagery.squareLogo} alt='Login Logo' />
                </div>
            ) : (
                <></>
            )}

            <h2 className='lf-title'>{language.get(Authentication.LoginForm_LoginTitle)}</h2>
            <h3 className='lf-subtitle'>{language.get(Authentication.LoginForm_WelcomeBack)}</h3>

            <FormProvider {...methods}>
                <form
                    className='lf-form'
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onSubmit={handleSubmit(successfulSubmit)}
                    noValidate
                    onKeyDown={(event: React.KeyboardEvent<HTMLFormElement>): void => {
                        if (event.key === KeyboardCode.Enter) {
                            event.preventDefault()
                            event.stopPropagation()

                            handleSubmit(successfulSubmit)
                        }
                    }}
                >
                    <ValidationErrors className='validation-error-container' name={USERNAME_FIELD_NAME}>
                        <OutlinedTextInput
                            labelLeft={language.get(Authentication.LoginForm_Username)}
                            placeholder={language.get(Authentication.LoginForm_UsernamePlaceholder)}
                            inputName={USERNAME_FIELD_NAME}
                            autoComplete='username'
                            disabled={
                                ((isLoginLandingPage || isLoginLandingPageALB) &&
                                    loginLandingPageView !== LoginState.VIEW_USERNAME) ||
                                isStringNotEmpty(usernameFromURL) ||
                                isLoadingServices
                            }
                            type='email'
                            rules={{
                                pattern: broadworksSSORegEx
                                    ? {
                                          value: broadworksSSORegEx,
                                          message: language.get(Authentication.LoginForm_InvalidEmail)
                                      }
                                    : undefined,
                                validate: {
                                    required: (value: string): ValidateResult => {
                                        if (isStringNotEmpty(value)) {
                                            return undefined
                                        }

                                        return language.get(Authentication.LoginForm_UsernameRequired)
                                    }
                                }
                            }}
                            adornmentEnd={isLoadingServices ? <Loader /> : <></>}
                        />
                    </ValidationErrors>

                    {(isLoginLandingPage || isLoginLandingPageALB) &&
                    loginLandingPageView === LoginState.VIEW_SERVER_CME &&
                    (displayServices?.length ?? 0) > 0 ? (
                        <>
                            <OutlinedSelect
                                containerClassName='lf-server-field'
                                name={SERVER_FIELD_NAME}
                                label={language.get(Authentication.LoginForm_Server)}
                                options={[
                                    {
                                        value: NONE_VALUE_STRING_DEFAULT,
                                        label: language.get(Authentication.LoginForm_ServerPlaceholder)
                                    },
                                    ...(displayServices?.map(
                                        (service: ServiceEntry): SelectOption<string> => ({
                                            value: service.url,
                                            label: `${service.description} (${service.url})`
                                        })
                                    ) ?? [])
                                ]}
                            />
                        </>
                    ) : (
                        <></>
                    )}

                    {isLoginLandingPageALB && loginLandingPageView === 1 ? (
                        <>
                            <OutlinedTextInput
                                className='lf-cme-field'
                                inputName={CME_FIELD_NAME}
                                labelLeft={`${
                                    (displayServices?.length ?? 0) > 0
                                        ? `${language.get(Authentication.LoginForm_Or)} `
                                        : EMPTY_STRING
                                }${language.get(Authentication.LoginForm_CME)}`}
                            />
                        </>
                    ) : (
                        <></>
                    )}

                    {isLandingView ? (
                        <></>
                    ) : (
                        <>
                            {!isBroadworksSignInMode ? (
                                <>
                                    <ValidationErrors className='validation-error-container' name={PASSWORD_FIELD_NAME}>
                                        <OutlinedPasswordTextInput
                                            labelLeft={language.get(Authentication.LoginForm_Password)}
                                            placeholder={language.get(Authentication.LoginForm_PasswordPlaceholder)}
                                            inputName={PASSWORD_FIELD_NAME}
                                            autoComplete={AutocompleteValues.CURRENT_PASSWORD}
                                            disabled={isLoadingServices}
                                            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>): void => {
                                                if (event.key === 'Enter') {
                                                    handleSubmit(successfulSubmit)()
                                                }
                                            }}
                                            rules={{
                                                validate: {
                                                    required: (value: string): ValidateResult => {
                                                        if (isStringNotEmpty(value)) {
                                                            return undefined
                                                        }

                                                        return language.get(Authentication.LoginForm_PasswordRequired)
                                                    }
                                                }
                                            }}
                                        />
                                    </ValidationErrors>
                                </>
                            ) : (
                                <></>
                            )}
                        </>
                    )}

                    <div className={`lf-additional-options ${isLandingView ? 'lfao-landing-view' : EMPTY_STRING}`}>
                        {isLandingView ? (
                            <></>
                        ) : (
                            <RouterCheckbox
                                id='remember-me-checkbox'
                                className='lf-remember-me-checkbox'
                                disabled={isLoadingServices}
                                labelRight={language.get(Authentication.LoginForm_RememberMe)}
                                name={REMEMBER_ME_FIELD_NAME}
                            />
                        )}

                        {!isBroadworksSignInMode ? (
                            <RouterAnchor
                                to='/forgotPassword'
                                className='lf-forgot-password'
                                disabled={isLoadingServices}
                            >
                                {language.get(Authentication.LoginForm_ForgotPassword)}
                            </RouterAnchor>
                        ) : (
                            <></>
                        )}
                    </div>

                    {!isBroadworksSignInMode ? (
                        <RoundedButton disabled={isLoadingServices} type='submit' className='lf-login-button'>
                            {language.get(
                                (isLoginLandingPage || isLoginLandingPageALB) &&
                                    (loginLandingPageView === 0 || loginLandingPageView === 1)
                                    ? Authentication.LoginForm_LandingPage_NextButton
                                    : Authentication.LoginForm_SignIn
                            )}
                        </RoundedButton>
                    ) : (
                        <></>
                    )}

                    <SignInWith
                        branding={branding}
                        setBroadworksSSORegEx={setBroadworksSSORegEx}
                        isBroadworksSignInMode={isBroadworksSignInMode}
                        setIsLoadingServices={setIsLoadingServices}
                        isLoadingServices={isLoadingServices}
                        landingPage={{
                            isEnabled: isLoginLandingPage || isLoginLandingPageALB,
                            performCheck: makeLoginLandingPageCheckRequest,
                            performLookup: performServiceLookup,
                            loginView: loginLandingPageView,
                            setValues: (username: string, hash: string) => {
                                setLoginLandingPageUrlRedirectHash(hash)
                                setValue('username', username)
                            }
                        }}
                        resetForm={clearErrors}
                    />

                    <input type='hidden' name={LOCALE_FIELD_NAME} ref={register()} />
                    <LocaleSwitcher
                        onChange={(locale: string): void => setValue(LOCALE_FIELD_NAME as keyof LoginFormType, locale)}
                    />
                </form>
            </FormProvider>
        </div>
    )
}
